/**
 * Success Measures Service
 *
 * API endpoints for success measures operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { SuccessMeasure, SuccessMeasureAmountType } from '@medsuite/models';

/**
 * Create success measure DTO
 */
export interface CreateSuccessMeasureDto {
  measure_type_id: number;
  title: string;
  alternate_value?: string | null;
  amount?: number | null;
  amount_type?: SuccessMeasureAmountType | null;
  period_start?: Date | null;
  period_end?: Date | null;
  is_one_time?: boolean | null;
}

/**
 * Update success measure DTO
 */
export interface UpdateSuccessMeasureDto {
  measure_type_id?: number;
  title?: string;
  alternate_value?: string | null;
  amount?: number | null;
  amount_type?: SuccessMeasureAmountType | null;
  period_start?: Date | null;
  period_end?: Date | null;
  is_one_time?: boolean | null;
}

/**
 * Success Measures Service
 */
class SuccessMeasuresService {
  /**
   * Get all success measures for an investment
   */
  async getAll(
    investmentId: number
  ): Promise<
    ApiResponse<{ investment_id: number; success_measures: SuccessMeasure[]; total: number }>
  > {
    return apiClient.get<{
      investment_id: number;
      success_measures: SuccessMeasure[];
      total: number;
    }>(`/investments/${investmentId}/success-measures`);
  }

  /**
   * Create a new success measure
   */
  async create(
    investmentId: number,
    data: CreateSuccessMeasureDto
  ): Promise<ApiResponse<SuccessMeasure>> {
    return apiClient.post<SuccessMeasure>(`/investments/${investmentId}/success-measures`, data);
  }

  /**
   * Update a success measure
   */
  async update(
    investmentId: number,
    successMeasureId: number,
    data: UpdateSuccessMeasureDto
  ): Promise<ApiResponse<SuccessMeasure>> {
    return apiClient.put<SuccessMeasure>(
      `/investments/${investmentId}/success-measures/${successMeasureId}`,
      data
    );
  }

  /**
   * Delete a success measure
   */
  async delete(investmentId: number, successMeasureId: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(
      `/investments/${investmentId}/success-measures/${successMeasureId}`
    );
  }
}

export const successMeasuresService = new SuccessMeasuresService();
